import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertCircle, ArrowDown, BarChart3, BookMarked, Check, CheckCircle2, Crown,
  Eye, FileText, Lightbulb, Loader2, RefreshCw, Search, Sparkles, Target,
  Users, Wand2, X, Zap
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import ShweebumCat from "../ShweebumCat";

export const StoryDoctorModal = ({
  open,
  onOpenChange,
  smartAnalysis,
  setSmartAnalysis,
  previewFix,
  setPreviewFix,
  doctorTab,
  setDoctorTab,
  issues,
  setIssues,
  selectedIssues,
  setSelectedIssues,
  analyzingMode,
  scanning,
  deepFixing,
  isLongStory,
  applyingFixIdx,
  applyingAllFixes,
  project,
  projectId,
  API,
  getScoreColor,
  handleSmartAnalysis,
  handleCharacterAnalysis,
  handleScanForIssues,
  handleFixIssue,
  handleFixAllIssues,
  handleDeepFix,
  handleFixSingleIssue,
  handleFixSelectedIssues,
  handleApplyPreviewedFix,
  handleApplyAllAnalysisFixes,
  handleFixRecommendation,
  fixingIssue,
}) => {
  return (
      <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setSmartAnalysis(null); setPreviewFix(null); setDoctorTab('overview'); } }}>
        <DialogContent className="sm:max-w-6xl flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-rose-600" />
              Story Analysis
              {smartAnalysis && (
                <Badge className={`ml-2 ${getScoreColor(smartAnalysis.overall_score)}`}>
                  {smartAnalysis.overall_score}/10
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Analyzes your story and suggests improvements
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Analysis Mode Selection */}
            {!smartAnalysis && !analyzingMode && !scanning && !deepFixing && issues.length === 0 && (
              <div className="py-6">
                <div className="text-center mb-6">
                  <ShweebumCat size="md" />
                  <p className="text-muted-foreground mt-3">Choose your analysis type</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                  <Card className="cursor-pointer hover:border-rose-400 transition-colors" onClick={handleSmartAnalysis}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-rose-100 rounded-lg"><Sparkles className="w-5 h-5 text-rose-600" /></div>
                        <h4 className="font-semibold text-foreground">Comprehensive Analysis</h4>
                      </div>
                      <p className="text-sm text-muted-foreground">Chapter-by-chapter scoring with feedback on pacing, dialogue, tension, and characters.</p>
                      <div className="mt-3 flex flex-wrap gap-1">
                        <Badge variant="outline" className="text-xs">Chapter Scores</Badge>
                        <Badge variant="outline" className="text-xs">Plot Threads</Badge>
                        <Badge variant="outline" className="text-xs">Before/After</Badge>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="cursor-pointer hover:border-purple-400 transition-colors" onClick={handleCharacterAnalysis}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-purple-100 rounded-lg"><Users className="w-5 h-5 text-purple-600" /></div>
                        <h4 className="font-semibold text-foreground">Character Analysis</h4>
                      </div>
                      <p className="text-sm text-muted-foreground">Deep dive into character consistency - voice, behavior, and development arcs.</p>
                      <div className="mt-3 flex flex-wrap gap-1">
                        <Badge variant="outline" className="text-xs">Voice</Badge>
                        <Badge variant="outline" className="text-xs">Behavior</Badge>
                        <Badge variant="outline" className="text-xs">Arc</Badge>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="cursor-pointer hover:border-cyan-500/35 transition-colors" onClick={handleScanForIssues}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-slate-800/60 rounded-lg"><Search className="w-5 h-5 text-cyan-400" /></div>
                        <h4 className="font-semibold text-foreground">Quick Scan</h4>
                      </div>
                      <p className="text-sm text-muted-foreground">Fast scan for typos, name inconsistencies, grammar issues.</p>
                    </CardContent>
                  </Card>
                  
                  {!isLongStory && (
                    <Card className="cursor-pointer hover:border-green-400 transition-colors" onClick={handleDeepFix}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 bg-green-100 rounded-lg"><Wand2 className="w-5 h-5 text-green-600" /></div>
                          <h4 className="font-semibold text-foreground">Auto-Fix Everything</h4>
                        </div>
                        <p className="text-sm text-muted-foreground">Automatically fix all issues and polish your story.</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}
            
            {/* Loading States */}
            {(analyzingMode || scanning || deepFixing) && (
              <div className="text-center py-12">
                <ShweebumCat size="lg" speaking />
                <p className="text-foreground font-medium mt-4">
                  {analyzingMode === 'smart' && 'Running comprehensive analysis...'}
                  {analyzingMode === 'characters' && 'Analyzing character consistency...'}
                  {scanning && 'Scanning for issues...'}
                  {deepFixing && 'Performing deep revision...'}
                </p>
                <div className="mt-6 w-48 bg-slate-700 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-rose-500 via-purple-500 to-blue-500 animate-pulse" style={{width: '60%'}}></div>
                </div>
              </div>
            )}
            
            {/* Smart Analysis Results */}
            {smartAnalysis && !analyzingMode && (
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="flex border-b border-border mb-4 overflow-x-auto">
                  {[
                    { id: 'overview', label: 'Overview', icon: '📊' },
                    { id: 'chapters', label: 'Chapters', icon: '📖' },
                    { id: 'issues', label: `Issues (${smartAnalysis.all_issues?.length || 0})`, icon: '⚠️' },
                    { id: 'plot', label: 'Plot Threads', icon: '🧵' },
                    { id: 'characters', label: 'Characters', icon: '👥' },
                  ].map(tab => (
                    <button key={tab.id} onClick={() => setDoctorTab(tab.id)} className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${doctorTab === tab.id ? 'border-b-2 border-rose-500 text-rose-700' : 'text-muted-foreground hover:text-slate-200'}`}>{tab.icon} {tab.label}</button>
                  ))}
                </div>
                
                <div className="flex-1 overflow-y-auto">
                  {doctorTab === 'overview' && (
                    <div className="space-y-4">
                      <Card className="bg-gradient-to-r from-rose-50 to-purple-50 border-rose-200">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div><h3 className="text-lg font-semibold text-foreground">Overall Story Score</h3><p className="text-sm text-muted-foreground">Based on {smartAnalysis.total_chapters_analyzed} chapter(s)</p></div>
                            <div className={`text-4xl font-bold px-4 py-2 rounded-xl ${getScoreColor(smartAnalysis.overall_score)}`}>{smartAnalysis.overall_score}/10</div>
                          </div>
                        </CardContent>
                      </Card>
                      <div className="grid grid-cols-3 gap-3">
                        <Card className="bg-red-50 border-red-200"><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-red-700">{smartAnalysis.issue_count?.high || 0}</div><div className="text-xs text-red-600">High Priority</div></CardContent></Card>
                        <Card className="bg-slate-800/50 border-cyan-500/25"><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-cyan-300">{smartAnalysis.issue_count?.medium || 0}</div><div className="text-xs text-cyan-400">Medium</div></CardContent></Card>
                        <Card className="bg-blue-50 border-blue-200"><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-blue-700">{smartAnalysis.issue_count?.low || 0}</div><div className="text-xs text-blue-600">Low</div></CardContent></Card>
                      </div>
                      <div className="flex gap-2"><Button onClick={() => setDoctorTab('issues')} variant="outline" className="flex-1">View All Issues</Button><Button onClick={handleSmartAnalysis} variant="outline"><RefreshCw className="w-4 h-4 mr-1" />Re-analyze</Button></div>
                    </div>
                  )}
                  
                  {doctorTab === 'chapters' && smartAnalysis.chapter_analyses?.map((chapter, idx) => (
                    <Card key={idx} className={`mb-3 ${chapter.error ? 'border-red-500/30 bg-red-500/10' : 'bg-slate-800/50 border-slate-700'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div><h4 className="font-semibold text-white">Ch {chapter.chapter_number}: {chapter.chapter_title}</h4><p className="text-xs text-slate-400">{chapter.word_count} words</p></div>
                          {chapter.overall_score > 0 && <Badge className={getScoreColor(chapter.overall_score)}>{chapter.overall_score}/10</Badge>}
                        </div>
                        {chapter.scores && <div className="grid grid-cols-5 gap-2 mb-3">{Object.entries(chapter.scores).map(([key, value]) => (<div key={key} className="text-center"><div className={`text-sm font-bold rounded px-1 ${getScoreColor(value)}`}>{value}</div><div className="text-xs text-slate-400 capitalize">{key.replace('_', ' ')}</div></div>))}</div>}
                        {chapter.chapter_summary && <p className="text-sm text-slate-300 italic mb-2">"{chapter.chapter_summary}"</p>}
                        {chapter.strengths?.length > 0 && <div className="text-xs"><span className="font-medium text-emerald-400">Strengths:</span> <span className="text-slate-300">{chapter.strengths.join(' • ')}</span></div>}
                      </CardContent>
                    </Card>
                  ))}
                  
                  {doctorTab === 'issues' && (smartAnalysis.all_issues?.length === 0 ? <div className="text-center py-8"><Check className="w-12 h-12 text-emerald-500 mb-3" /><p className="text-slate-400">No issues found!</p></div> : (
                    <div>
                      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <p className="text-sm text-slate-400">Found {smartAnalysis.all_issues.length} issue(s)</p>
                        <div className="flex gap-2 flex-wrap">
                          {selectedIssues.size > 0 && (
                            <Button 
                              size="sm" 
                              className="bg-blue-600 hover:bg-blue-700" 
                              onClick={handleFixSelectedIssues}
                              disabled={applyingAllFixes}
                              data-testid="fix-selected-issues-btn"
                            >
                              {applyingAllFixes ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Saving...</> : `Save ${selectedIssues.size} Selected`}
                            </Button>
                          )}
                          {smartAnalysis.all_issues.length > 0 && (
                            <Button 
                              size="sm" 
                              className="bg-rose-600 hover:bg-rose-700" 
                              onClick={handleApplyAllAnalysisFixes}
                              disabled={applyingAllFixes}
                              data-testid="fix-all-issues-btn"
                            >
                              {applyingAllFixes ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Saving...</> : 'Save All to Story'}
                            </Button>
                          )}
                        </div>
                      </div>
                      {/* Select All / Deselect All */}
                      <div className="flex items-center gap-3 mb-3 text-xs">
                        <button 
                          className="text-cyan-400 hover:text-cyan-300 hover:underline"
                          onClick={() => setSelectedIssues(new Set(smartAnalysis.all_issues.map((_, i) => i)))}
                        >
                          Select All
                        </button>
                        <button 
                          className="text-slate-400 hover:text-slate-300 hover:underline"
                          onClick={() => setSelectedIssues(new Set())}
                        >
                          Deselect All
                        </button>
                        {selectedIssues.size > 0 && (
                          <span className="text-slate-500">({selectedIssues.size} selected)</span>
                        )}
                      </div>
                      {smartAnalysis.all_issues?.map((issue, idx) => (
                    <Card key={idx} className={`mb-3 border transition-all ${
                      selectedIssues.has(idx) ? 'border-blue-500 bg-blue-500/10 ring-2 ring-blue-500/30' :
                      issue.severity === 'high' ? 'border-red-500/40 bg-red-500/10' : 
                      issue.severity === 'medium' ? 'border-orange-500/40 bg-orange-500/10' : 
                      'border-slate-600 bg-slate-800/50'
                    }`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          {/* Checkbox for multi-select */}
                          <div className="pt-1">
                            <input
                              type="checkbox"
                              checked={selectedIssues.has(idx)}
                              onChange={(e) => {
                                const newSet = new Set(selectedIssues);
                                if (e.target.checked) {
                                  newSet.add(idx);
                                } else {
                                  newSet.delete(idx);
                                }
                                setSelectedIssues(newSet);
                              }}
                              className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500 cursor-pointer"
                              data-testid={`select-issue-${idx}`}
                            />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge variant="outline" className="text-xs capitalize border-slate-600 text-slate-300">{issue.type}</Badge>
                              <Badge variant="outline" className={`text-xs ${issue.severity === 'high' ? 'border-red-500/50 text-red-400 bg-red-500/10' : issue.severity === 'medium' ? 'border-orange-500/50 text-orange-400 bg-orange-500/10' : 'border-slate-600 text-slate-400'}`}>{issue.severity}</Badge>
                              {issue.chapter && <span className="text-xs text-slate-400">Ch {issue.chapter}</span>}
                              {issue.affects_future_chapters && <Badge variant="outline" className="text-xs border-purple-500/50 text-purple-400 bg-purple-500/10">Affects Future</Badge>}
                            </div>
                            <p className="text-sm font-medium text-white mb-2">{issue.description}</p>
                            <div className="bg-slate-700/50 rounded-md p-2 mb-2 border border-slate-600">
                              <p className="text-xs text-slate-300 italic">"{issue.location}"</p>
                            </div>
                            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-2">
                              <p className="text-xs text-emerald-300"><strong className="text-emerald-400">Suggested Fix:</strong> {issue.fix || issue.suggestion}</p>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2 shrink-0">
                            <Button 
                              size="sm" 
                              onClick={() => handleFixSingleIssue(idx)} 
                              disabled={applyingFixIdx !== null || applyingAllFixes}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`fix-issue-${idx}`}
                            >
                              {applyingFixIdx === idx ? (
                                <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Saving...</>
                              ) : (
                                'Save Fix'
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                      ))}
                    </div>
                  ))}
                  
                  {doctorTab === 'plot' && smartAnalysis.plot_threads && (
                    <div className="space-y-4">
                      {smartAnalysis.plot_threads.introduced?.length > 0 && <Card className="border-emerald-500/30 bg-emerald-500/10"><CardContent className="p-4"><h4 className="font-semibold text-emerald-400 mb-2">🌱 Introduced Threads</h4><ul className="list-disc list-inside text-sm text-slate-300 space-y-1">{smartAnalysis.plot_threads.introduced.map((t, i) => <li key={i}>{t}</li>)}</ul></CardContent></Card>}
                      {smartAnalysis.plot_threads.advanced?.length > 0 && <Card className="border-blue-500/30 bg-blue-500/10"><CardContent className="p-4"><h4 className="font-semibold text-blue-400 mb-2">📈 Advanced Threads</h4><ul className="list-disc list-inside text-sm text-slate-300 space-y-1">{smartAnalysis.plot_threads.advanced.map((t, i) => <li key={i}>{t}</li>)}</ul></CardContent></Card>}
                      {smartAnalysis.plot_threads.unresolved?.length > 0 && <Card className="border-orange-500/30 bg-orange-500/10"><CardContent className="p-4"><h4 className="font-semibold text-orange-400 mb-2">⚠️ Unresolved (Need Attention)</h4><ul className="list-disc list-inside text-sm text-slate-300 space-y-1">{smartAnalysis.plot_threads.unresolved.map((t, i) => <li key={i}>{t}</li>)}</ul></CardContent></Card>}
                    </div>
                  )}
                  
                  {doctorTab === 'characters' && (!smartAnalysis.characterAnalysis ? <div className="text-center py-8"><Users className="w-12 h-12 text-slate-500 mb-3" /><p className="text-slate-400 mb-4">Character analysis not yet run</p><Button onClick={handleCharacterAnalysis} disabled={analyzingMode === 'characters'}>{analyzingMode === 'characters' ? 'Analyzing...' : 'Analyze Characters'}</Button></div> : (
                    <div className="space-y-4">
                      {smartAnalysis.characterAnalysis.overall_character_score && <Card className="bg-purple-500/10 border-purple-500/30"><CardContent className="p-4"><div className="flex items-center justify-between"><span className="font-semibold text-purple-300">Overall Character Score</span><Badge className={getScoreColor(smartAnalysis.characterAnalysis.overall_character_score)}>{smartAnalysis.characterAnalysis.overall_character_score}/10</Badge></div></CardContent></Card>}
                      {smartAnalysis.characterAnalysis.characters?.map((char, idx) => (
                        <Card key={idx} className="bg-slate-800/50 border-slate-700"><CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3"><h4 className="font-semibold text-white">{char.name}</h4><div className="flex gap-1"><Badge className={getScoreColor(char.voice_score)} title="Voice">V:{char.voice_score}</Badge><Badge className={getScoreColor(char.behavior_score)} title="Behavior">B:{char.behavior_score}</Badge><Badge className={getScoreColor(char.development_score)} title="Development">D:{char.development_score}</Badge></div></div>
                          {char.arc_summary && <p className="text-sm text-slate-300 italic mb-2">"{char.arc_summary}"</p>}
                          {char.speech_patterns?.length > 0 && <div className="text-xs mb-2"><span className="font-medium text-slate-400">Speech:</span> <span className="text-slate-300">{char.speech_patterns.join(', ')}</span></div>}
                          {char.inconsistencies?.length > 0 && char.inconsistencies.map((inc, i) => <div key={i} className="text-xs bg-orange-500/10 p-2 rounded border border-orange-500/30 mt-1"><strong className="text-orange-400">{inc.type}:</strong> <span className="text-slate-300">{inc.description}</span></div>)}
                        </CardContent></Card>
                      ))}
                      {smartAnalysis.characterAnalysis.recommendations?.length > 0 && <Card className="border-emerald-500/30 bg-emerald-500/10"><CardContent className="p-4"><h4 className="font-semibold text-emerald-400 mb-2">Recommendations</h4><div className="space-y-2">{smartAnalysis.characterAnalysis.recommendations.map((r, i) => <div key={i} className="flex items-start justify-between gap-2 p-2 bg-slate-800/50 rounded border border-slate-700"><span className="text-sm text-slate-300 flex-1">{r}</span><Button size="sm" variant="outline" className="shrink-0 text-xs bg-emerald-600 text-white hover:bg-emerald-500 border-0" onClick={() => handleFixRecommendation(r)} disabled={fixingIssue !== null}>{fixingIssue === 'recommendation' ? 'Fixing...' : 'Fix This'}</Button></div>)}</div></CardContent></Card>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Quick Scan Results */}
            {!smartAnalysis && !scanning && issues.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-4"><p className="text-sm text-muted-foreground">Found {issues.length} issue(s)</p><div className="flex gap-2"><Button variant="outline" size="sm" onClick={handleScanForIssues}><RefreshCw className="w-3 h-3 mr-1" />Re-scan</Button>{issues.length > 1 && <Button size="sm" className="bg-rose-600" onClick={handleFixAllIssues} disabled={fixingIssue !== null}>Fix All</Button>}</div></div>
                {issues.map((issue, index) => (
                  <Card key={index} className={`border ${severityColors[issue.severity]}`}><CardContent className="p-4"><div className="flex items-start justify-between gap-3"><div className="flex-1"><div className="flex items-center gap-2 mb-1"><span className="text-lg">{issueTypeIcons[issue.type] || "⚠️"}</span><Badge variant="outline" className="capitalize text-xs">{issue.type.replace("_", " ")}</Badge><Badge variant="outline" className={`text-xs ${issue.severity === "high" ? "border-red-400 text-red-700" : issue.severity === "medium" ? "border-cyan-500/35 text-cyan-300" : "border-blue-400 text-blue-700"}`}>{issue.severity}</Badge></div><p className="text-sm font-medium text-foreground mb-1">{issue.description}</p><p className="text-xs text-muted-foreground italic mb-2">"{issue.location}"</p><p className="text-xs text-muted-foreground"><strong>Suggestion:</strong> {issue.suggestion}</p></div><Button size="sm" onClick={() => handleFixIssue(issue, index)} disabled={fixingIssue !== null} className="bg-green-600 hover:bg-green-700 shrink-0">{fixingIssue === index ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Fixing...</> : <>Fix</>}</Button></div></CardContent></Card>
                ))}
              </div>
            )}
          </div>
          
          {/* Preview Fix Modal */}
          {previewFix && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <Card className="w-full sm:max-w-lg"><CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><Eye className="w-5 h-5 text-blue-600" />Preview Fix</h3>
                {previewFix.loading ? <div className="text-center py-8"><RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mb-2" /><p className="text-muted-foreground">Generating preview...</p></div> : previewFix.error ? <div className="text-center py-8"><p className="text-red-600 mb-4">Could not generate preview</p><Button variant="outline" onClick={() => setPreviewFix(null)}>Close</Button></div> : (
                  <>
                    <div className="space-y-3 mb-4">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3"><p className="text-xs font-medium text-red-700 mb-1">Before:</p><p className="text-sm text-red-900">{previewFix.before}</p></div>
                      <div className="flex justify-center"><ArrowDown className="w-5 h-5 text-muted-foreground" /></div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3"><p className="text-xs font-medium text-green-700 mb-1">After:</p><p className="text-sm text-green-900">{previewFix.after}</p></div>
                    </div>
                    {previewFix.explanation && <p className="text-xs text-muted-foreground mb-4 italic"><strong>Why:</strong> {previewFix.explanation}</p>}
                    <div className="flex gap-2"><Button variant="outline" className="flex-1" onClick={() => setPreviewFix(null)}>Cancel</Button><Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleApplyPreviewedFix} disabled={applyingFixIdx !== null || !previewFix.can_apply}>{applyingFixIdx !== null ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />Applying...</> : 'Apply Fix'}</Button></div>
                    {!previewFix.can_apply && <p className="text-xs text-cyan-400 mt-2 text-center">⚠️ Could not locate exact text</p>}
                  </>
                )}
              </CardContent></Card>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { onOpenChange(false); setSmartAnalysis(null); setPreviewFix(null); setIssues([]); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


  );
};
